// src/components/dashboard/ActivityLog.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  where,
} from "firebase/firestore";

export const ACTIVITY_TYPES = {
  // Sample Orders
  SAMPLE_ORDER_STATUS_UPDATED: "SAMPLE_ORDER_STATUS_UPDATED",
  SAMPLE_ORDER_ASSIGNED_UPDATED: "SAMPLE_ORDER_ASSIGNED_UPDATED",
  SAMPLE_ORDER_NOTES_UPDATED: "SAMPLE_ORDER_NOTES_UPDATED",
  SAMPLE_ORDER_DELIVERED: "SAMPLE_ORDER_DELIVERED",

  // Tasks
  TASK_STATUS_UPDATED: "TASK_STATUS_UPDATED",
  TASK_ATTACHMENT_ADDED: "TASK_ATTACHMENT_ADDED",

  // Other
  ACCOUNT_CREATED: "ACCOUNT_CREATED",
  GENERIC: "GENERIC",
};

// Category colors with dark variants for account names
const CATEGORY_COLORS = {
  "Sample Orders": {
    light: "bg-blue-100",
    medium: "text-blue-700",
    dark: "text-blue-900",
  },
  "Tasks": {
    light: "bg-green-100", 
    medium: "text-green-700",
    dark: "text-green-900",
  },
  "Accounts": {
    light: "bg-purple-100",
    medium: "text-purple-700",
    dark: "text-purple-900",
  },
  "Other": {
    light: "bg-gray-100",
    medium: "text-gray-700",
    dark: "text-gray-900",
  },
};

// ✅ Call this from anywhere you write changes
export async function logActivity({
  companyId,
  type = ACTIVITY_TYPES.GENERIC,
  actor,
  data = {},
  account,
  territory,
  message,
  entity,
}) {
  if (!companyId) throw new Error("logActivity: companyId is required");

  const db = getFirestore();

  const payload = {
    type,
    message: message || null,
    data: data || {},
    entity: entity || null,
    account: account || null,
    territory: territory || null,
    actor: actor
      ? {
          uid: actor.uid || null,
          name: actor.name || actor.displayName || "Unknown",
          email: actor.email || null,
        }
      : null,
    createdAt: serverTimestamp(),
  };

  const ref = collection(db, "companies", companyId, "activity");
  await addDoc(ref, payload);
}

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
  if (!d) return "";
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 5) return "now";
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function buildMessage(item) {
  if (item?.message) return item.message;

  const actor = item?.actor?.name ? item.actor.name : "User";
  const type = item?.type;
  const data = item?.data || {};
  const accountName = item?.account?.name || data?.accountName || "";
  const status = data?.status || data?.newStatus || "";

  switch (type) {
    case ACTIVITY_TYPES.TASK_STATUS_UPDATED: {
      const taskTitle = data.taskTitle || item?.entity?.title || "a task";
      const status = data.status || "updated";
      return `${actor} updated ${taskTitle} to ${status}`;
    }
    case ACTIVITY_TYPES.SAMPLE_ORDER_STATUS_UPDATED: {
      if (accountName && status) {
        return `${actor} updated the status of ${accountName} to ${status}`;
      }
      return `${actor} updated a sample order`;
    }
    case ACTIVITY_TYPES.SAMPLE_ORDER_ASSIGNED_UPDATED: {
      if (accountName) {
        return `${actor} updated the assigned rep for ${accountName}`;
      }
      return `${actor} updated assignment for a sample order`;
    }
    case ACTIVITY_TYPES.SAMPLE_ORDER_NOTES_UPDATED: {
      if (accountName) {
        return `${actor} updated notes for ${accountName}`;
      }
      return `${actor} updated sample order notes`;
    }
    case ACTIVITY_TYPES.SAMPLE_ORDER_DELIVERED: {
      if (accountName) {
        return `${actor} delivered sample order to ${accountName}`;
      }
      return `Sample order delivered`;
    }
    case ACTIVITY_TYPES.ACCOUNT_CREATED: {
      const terr = item?.territory?.name || data.territoryName || "a territory";
      return `${actor} created an account in ${terr}`;
    }
    default: {
      const label = item?.entity?.title || data?.title || data?.name || "";
      return `${actor} updated ${label || "item"}`;
    }
  }
}

function typeLabel(t) {
  switch (t) {
    case ACTIVITY_TYPES.SAMPLE_ORDER_STATUS_UPDATED:
    case ACTIVITY_TYPES.SAMPLE_ORDER_ASSIGNED_UPDATED:
    case ACTIVITY_TYPES.SAMPLE_ORDER_NOTES_UPDATED:
    case ACTIVITY_TYPES.SAMPLE_ORDER_DELIVERED:
      return "Sample Orders";

    case ACTIVITY_TYPES.TASK_STATUS_UPDATED:
    case ACTIVITY_TYPES.TASK_ATTACHMENT_ADDED:
      return "Tasks";

    case ACTIVITY_TYPES.ACCOUNT_CREATED:
      return "Accounts";

    default:
      return "Other";
  }
}

export default function ActivityLog({
  companyId,
  title = "Activity Feed",
  pageSize = 6,
  showControls = true,
  maxHeight = "400px"
}) {
  const db = useMemo(() => getFirestore(), []);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [hasMore, setHasMore] = useState(true);

  const lastDocRef = useRef(null);
  const unsubscribeRef = useRef(null);

  const activityCol = useMemo(() => {
    if (!companyId) return null;
    return collection(db, "companies", companyId, "activity");
  }, [db, companyId]);

  const filterToTypes = useMemo(() => {
    switch (filter) {
      case "TASKS":
        return [ACTIVITY_TYPES.TASK_STATUS_UPDATED, ACTIVITY_TYPES.TASK_ATTACHMENT_ADDED];
      case "SAMPLE_ORDERS":
        return [
          ACTIVITY_TYPES.SAMPLE_ORDER_STATUS_UPDATED,
          ACTIVITY_TYPES.SAMPLE_ORDER_ASSIGNED_UPDATED,
          ACTIVITY_TYPES.SAMPLE_ORDER_NOTES_UPDATED,
          ACTIVITY_TYPES.SAMPLE_ORDER_DELIVERED,
        ];
      case "ACCOUNTS":
        return [ACTIVITY_TYPES.ACCOUNT_CREATED];
      case "OTHER":
        return [ACTIVITY_TYPES.GENERIC];
      default:
        return null;
    }
  }, [filter]);

  useEffect(() => {
    if (!activityCol) return;

    setLoading(true);
    setHasMore(true);
    lastDocRef.current = null;

    if (unsubscribeRef.current) {
      try {
        unsubscribeRef.current();
      } catch {}
    }

    const constraints = [orderBy("createdAt", "desc"), limit(pageSize)];
    if (filterToTypes && filterToTypes.length) {
      constraints.unshift(where("type", "in", filterToTypes));
    }

    const q = query(activityCol, ...constraints);

    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(next);
        setLoading(false);

        lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
        setHasMore(snap.size === pageSize);
      },
      (err) => {
        console.error("ActivityLog onSnapshot error:", err);
        setLoading(false);
      }
    );

    unsubscribeRef.current = unsub;
    return () => {
      if (unsubscribeRef.current) {
        try {
          unsubscribeRef.current();
        } catch {}
        unsubscribeRef.current = null;
      }
    };
  }, [activityCol, pageSize, filterToTypes]);

  async function loadMore() {
    if (!activityCol || !hasMore || !lastDocRef.current) return;

    setLoadingMore(true);
    try {
      const constraints = [
        orderBy("createdAt", "desc"),
        startAfter(lastDocRef.current),
        limit(pageSize),
      ];
      if (filterToTypes && filterToTypes.length) {
        constraints.unshift(where("type", "in", filterToTypes));
      }

      const q = query(activityCol, ...constraints);
      const snap = await getDocs(q);

      const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const merged = [...prev];
        for (const n of next) if (!seen.has(n.id)) merged.push(n);
        return merged;
      });

      lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
      setHasMore(snap.size === pageSize);
    } catch (e) {
      console.error("ActivityLog loadMore error:", e);
    } finally {
      setLoadingMore(false);
    }
  }

  const filteredItems = items;

  const FILTER_OPTIONS = [
    { value: "ALL", label: "All Activity" },
    { value: "TASKS", label: "Tasks" },
    { value: "SAMPLE_ORDERS", label: "Sample Orders" },
    { value: "ACCOUNTS", label: "Accounts" },
    { value: "OTHER", label: "Other" },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          </div>

          {showControls && (
            <div className="flex items-center gap-2">
              {/* Filter Dropdown */}
              <div className="relative">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="pl-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  {FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity List Container */}
      <div className="relative" style={{ maxHeight }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading activity...</p>
            </div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <div className="text-center">
              <p className="text-gray-500 font-medium">No activity yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Changes will appear here when you create or update items
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-y-auto" style={{ maxHeight }}>
            <ul className="divide-y divide-gray-100">
              {filteredItems.map((it) => {
                const category = typeLabel(it?.type);
                const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS["Other"];
                
                return (
                  <li 
                    key={it.id} 
                    className="px-4 py-2 hover:bg-gray-50/80 transition-colors group"
                  >
                    {/* SINGLE LINE - NO WRAPPING */}
                    <div className="flex items-center justify-between gap-3 overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {/* Category badge */}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${colors.light} ${colors.medium} whitespace-nowrap flex-shrink-0`}>
                          {category}
                        </span>
                        
                        {/* Message on the line */}
                        <div className="text-sm text-gray-900 truncate">
                          {buildMessage(it)}
                        </div>
                      </div>
                      
                      {/* Time */}
                      <span className="text-xs text-gray-500 font-medium whitespace-nowrap flex-shrink-0">
                        {timeAgo(it?.createdAt)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Load More */}
            {hasMore && !loading && filteredItems.length >= pageSize && (
              <div className="px-4 py-3 border-t border-gray-100">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="w-full text-sm px-4 py-2 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                >
                  {loadingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                      Loading...
                    </>
                  ) : (
                    "Load more activity"
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {!loading && filteredItems.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{filteredItems.length} activities</span>
            {!hasMore && filteredItems.length >= pageSize && (
              <span className="text-gray-400">All activity loaded</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
